import { useEffect, useRef } from 'react';

import { GAME_TYPES } from '@/features/gameSession/store/constants';
import { toGameSessionQuestion } from '@/features/bouncySums/helpers';
import { EventBus, EVENTS } from '@/features/bouncySums/game/events/bouncySumsEventBus';
import { EndOutcome } from '@/features/gameSession/types';

import type {
	BouncySumsSceneAnswerEvent,
	BouncySumsSceneFinishEvent,
	BouncySumsSceneStateEvent,
} from '@/features/bouncySums/types';
import type { GameAnswerEvent, GameCloseEvent, GameFinishEvent } from '@/features/gameSession/types';

type UseBouncySumsSceneEventsParams = {
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onClose: (event?: GameCloseEvent) => void;
	onStateChange: (event: BouncySumsSceneStateEvent) => void;
	topicTitle: string;
};

export function useBouncySumsSceneEvents({
	onAnswer,
	onClose,
	onFinish,
	onStateChange,
	topicTitle,
}: UseBouncySumsSceneEventsParams): void {
	const onAnswerRef = useRef(onAnswer);
	const onFinishRef = useRef(onFinish);
	const onCloseRef = useRef(onClose);
	const onStateChangeRef = useRef(onStateChange);

	useEffect(() => {
		onAnswerRef.current = onAnswer;
	}, [onAnswer]);

	useEffect(() => {
		onFinishRef.current = onFinish;
	}, [onFinish]);

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		onStateChangeRef.current = onStateChange;
	}, [onStateChange]);

	useEffect(() => {
		const handleAnswer = (event: BouncySumsSceneAnswerEvent) => {
			const question = toGameSessionQuestion(event.question, event.questionIndex, topicTitle);
			const answer = question.answers?.[0];

			onAnswerRef.current?.({
				gameType: GAME_TYPES.BOUNCY_SUMS_GAME,
				question,
				questionIndex: event.questionIndex,
				attemptNumber: event.attemptNumber,
				isCorrect: event.isCorrect,
				answer,
				answerId: event.question.id,
				answerTexts: [event.currentAnswer],
				answerTimeInSeconds: Math.max(0, Math.round(event.answerRecord.answer_time_in_ms / 1000)),
				score: event.score,
				data: {
					bpm: event.bpm,
					madeMistake: event.madeMistake,
					speedChange: event.speedChange,
					table: event.answerRecord.table,
					question: event.answerRecord.question,
				},
			});
		};
		const handleFinish = (event: BouncySumsSceneFinishEvent) => {
			onFinishRef.current?.({
				gameOutcome: event.finished ? EndOutcome.SUCCEEDED : EndOutcome.ABANDONED,
				score: event.score,
				data: { ...event },
			});
		};
		const handleClose = (event: BouncySumsSceneFinishEvent) => {
			onCloseRef.current?.({
				finishEvent: {
					gameOutcome: EndOutcome.ABANDONED,
					score: event.score,
					data: { ...event },
				},
			});
		};
		const handleStateChange = (event: BouncySumsSceneStateEvent) => {
			onStateChangeRef.current(event);
		};

		EventBus.on(EVENTS.ANSWER, handleAnswer);
		EventBus.on(EVENTS.FINISH, handleFinish);
		EventBus.on(EVENTS.CLOSE, handleClose);
		EventBus.on(EVENTS.STATE_CHANGE, handleStateChange);

		return () => {
			EventBus.off(EVENTS.ANSWER, handleAnswer);
			EventBus.off(EVENTS.FINISH, handleFinish);
			EventBus.off(EVENTS.CLOSE, handleClose);
			EventBus.off(EVENTS.STATE_CHANGE, handleStateChange);
		};
	}, [topicTitle]);
}
